import '@draft-js-plugins/mention/lib/plugin.css';
import '@draft-js-plugins/emoji/lib/plugin.css';
import './Editor.scss';
import classNames from 'classnames';
import { COPY_GENERIC } from '@sonar-software/sonar-ts-constants';
import DraftEditor from '@draft-js-plugins/editor';
import draftToHtml from 'draftjs-to-html';
import { EditOutlined } from '@ant-design/icons';
import { getClosestTableBody } from '@lib/popups';
import groupBy from 'lodash/groupBy';
import { OutsideClickWrapper } from './general';
import { useEditorToolbar } from '../hooks/useEditorToolbar';
import values from 'lodash/values';
import { Button, Popover } from 'antd';
import { ContentState, convertFromRaw, convertToRaw, EditorState, getDefaultKeyBinding, KeyBindingUtil } from 'draft-js';
import { defaultSuggestionsFilter } from '@draft-js-plugins/mention';
import { Else, If, Then, When } from 'react-if';
import { getFullName, isJSON } from '@sonar-software/sonar-ts-lib';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useEditorMentions, useUsers } from '@hooks';
const { createWithContent, moveSelectionToEnd, push, createEmpty, moveFocusToEnd } = EditorState;
const { createFromText } = ContentState;
const { isSoftNewlineEvent } = KeyBindingUtil;
export function getInitialEditorState(content) {
    if (typeof content === 'string') {
        const contentState = isJSON(content) ? convertFromRaw(JSON.parse(content)) : createFromText(content);
        const editorState = createWithContent(contentState);
        return moveSelectionToEnd(editorState);
    }
    return createEmpty();
}
function htmlTransform(entity) {
    if (entity.type === 'mention') {
        const { name } = entity.data.mention;
        return `<span class="mention">@${name}</span>`;
    }
    return undefined;
}
function getUserSuggestions(users) {
    return users
        .filter((user) => !user.isLocked)
        .map((user) => ({
        email: user.email,
        id: user.id,
        name: getFullName(user.firstName, user.lastName),
        userId: user.id
    }));
}
function getTaggedUsers(rawContent) {
    var _a;
    const taggedUsers = (_a = groupBy(rawContent.entityMap, 'type').mention) === null || _a === void 0 ? void 0 : _a.reduce((tagged, entity) => {
        const { userId, email } = entity.data.mention;
        if (!tagged[userId]) {
            tagged[userId] = {
                email,
                userId
            };
        }
        return tagged;
    }, {});
    return values(taggedUsers || []);
}
export function hasContent(editorState) {
    const contentState = editorState.getCurrentContent();
    const firstBlock = contentState.getBlockMap().first();
    const hasLeadingStyleElement = firstBlock.getType() !== 'unstyled';
    const hasText = contentState.hasText();
    return hasText || hasLeadingStyleElement;
}
export const Editor = forwardRef(({ bordered, canBeEmpty, className, content, contentCheck, editing, editOnClick, enableMentions = false, getPopupContainer, isForm, isReadOnly, isSpreadsheet, onBlur, onCancel, onFocus, onSave, placeholder, saveOnBlur, showEditButton }, ref) => {
    const { plugins, toolbar } = useEditorToolbar();
    const { MentionSuggestions, plugins: modifiedPlugins } = useEditorMentions(plugins, enableMentions);
    const [editorState, setEditorState] = useState(getInitialEditorState(content));
    const [focused, setFocused] = useState(false);
    const editorRef = useRef(null);
    const users = useUsers();
    const [suggestions, setSuggetions] = useState([]);
    const [visibleSuggestions, setVisibleSuggestions] = useState(suggestions);
    const [showSuggestions, setShowSuggestions] = useState(false);
    useEffect(() => {
        const mappedUsers = getUserSuggestions(users);
        setSuggetions(mappedUsers);
        setVisibleSuggestions(mappedUsers);
    }, [users]);
    const onSearchChange = useCallback(({ value }) => {
        if (suggestions) {
            setVisibleSuggestions(defaultSuggestionsFilter(value, suggestions));
        }
    }, [suggestions]);
    const handleStartEditing = useCallback((focusToEnd) => {
        setFocused(true);
        setTimeout(() => {
            var _a;
            if (focusToEnd) {
                setEditorState((state) => moveFocusToEnd(state));
            }
            else {
                (_a = editorRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            }
        }, 10);
    }, []);
    const handleSaveChanges = useCallback(() => {
        var _a;
        const rawContent = convertToRaw(editorState.getCurrentContent());
        const html = draftToHtml(rawContent, {}, false, htmlTransform);
        onSave(JSON.stringify(rawContent), html, getTaggedUsers(rawContent));
        if (isForm) {
            setEditorState(EditorState.createEmpty());
        }
        setFocused(false);
        (_a = editorRef.current) === null || _a === void 0 ? void 0 : _a.blur();
    }, [editorState, isForm, onSave]);
    const handleCancelChanges = useCallback(() => {
        var _a;
        (_a = editorRef.current) === null || _a === void 0 ? void 0 : _a.blur();
        if (onCancel) {
            onCancel();
        }
        setEditorState(isForm ? EditorState.createEmpty() : getInitialEditorState(content));
        setFocused(false);
    }, [content, isForm, onCancel]);
    useEffect(() => {
        if (editing) {
            handleStartEditing();
        }
    }, [editing, handleStartEditing]);
    useEffect(() => {
        if (content) {
            const contentState = isJSON(content) ? convertFromRaw(JSON.parse(content)) : createFromText(content);
            setEditorState((prevEditorState) => push(prevEditorState, contentState, 'insert-characters'));
        }
    }, [content]);
    const editorHasContent = hasContent(editorState);
    const isActive = focused || Boolean(editing);
    const isClickable = !isReadOnly && (isActive || Boolean(isForm) || !editorHasContent);
    const shouldShowEditButton = !isReadOnly && Boolean(showEditButton) && !isClickable;
    useEffect(() => {
        if (contentCheck) {
            contentCheck(editorHasContent);
        }
    }, [contentCheck, editorHasContent]);
    const handleGeneralOnClick = useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
        if (!isActive && editOnClick && !isReadOnly) {
            handleStartEditing(event.type === 'focus' && Boolean(event.relatedTarget));
        }
    }, [editOnClick, handleStartEditing, isActive, isReadOnly]);
    const handleOnFocus = (event) => {
        if (onFocus) {
            onFocus();
        }
        handleGeneralOnClick(event);
    };
    const handleOnClickOutside = useCallback(() => {
        if (isActive && saveOnBlur) {
            handleSaveChanges();
        }
    }, [handleSaveChanges, isActive, saveOnBlur]);
    useImperativeHandle(ref, () => ({
        edit: handleStartEditing,
        save: handleSaveChanges
    }));
    const outsideClickClassnames = classNames('draft', {
        'active': isActive,
        bordered,
        'editOnClick': editOnClick,
        'empty': !editorHasContent,
        'isForm': isForm,
        'isReadOnly': isReadOnly,
        'outside-click': isSpreadsheet,
        'spreadsheet': isSpreadsheet
    }, className);
    const onKeyCommand = (command) => {
        if (command === 'enter_command') {
            handleSaveChanges();
            return 'handled';
        }
        return 'not-handled';
    };
    const myKeyBindingFn = (e) => {
        if (e.code === 'Escape') {
            handleCancelChanges();
            return;
        }
        if (isSpreadsheet && e.code === 'Enter' && !isSoftNewlineEvent(e)) {
            return 'enter_command';
        }
        if (isSpreadsheet && e.code === 'Tab') {
            setTimeout(() => handleSaveChanges(), 10);
        }
        return getDefaultKeyBinding(e);
    };
    const draftEditorComponent = React.createElement(React.Fragment, null,
        React.createElement(DraftEditor, { editorState: editorState, handleKeyCommand: onKeyCommand, keyBindingFn: myKeyBindingFn, onBlur: onBlur, onChange: setEditorState, onFocus: handleOnFocus, placeholder: editorHasContent ? '' : placeholder, plugins: modifiedPlugins, readOnly: isReadOnly, ref: editorRef }),
        React.createElement(MentionSuggestions, { onOpenChange: setShowSuggestions, onSearchChange: onSearchChange, open: showSuggestions, suggestions: visibleSuggestions }),
        React.createElement(EditOutlined, { className: 'edit-icon' }));
    return (React.createElement(OutsideClickWrapper, { className: outsideClickClassnames, onClick: handleGeneralOnClick, onClickOutside: handleOnClickOutside },
        React.createElement(If, { condition: isSpreadsheet },
            React.createElement(Then, null,
                React.createElement(Popover, { classNames: { root: 'editor-popover' }, content: toolbar, getPopupContainer: getPopupContainer !== null && getPopupContainer !== void 0 ? getPopupContainer : getClosestTableBody, open: isActive, placement: 'bottom' }, draftEditorComponent)),
            React.createElement(Else, null,
                draftEditorComponent,
                React.createElement(If, { condition: isActive },
                    React.createElement(Then, null,
                        toolbar,
                        React.createElement(Button, { className: 'action', disabled: !canBeEmpty && !editorHasContent, onClick: handleSaveChanges, type: 'primary' }, COPY_GENERIC.SAVE),
                        React.createElement(Button, { className: 'action', onClick: handleCancelChanges }, COPY_GENERIC.CANCEL)),
                    React.createElement(Else, null,
                        React.createElement(When, { condition: shouldShowEditButton && !editOnClick },
                            React.createElement(EditOutlined, { className: 'edit-button', onClick: () => handleStartEditing() }))))))));
});
